import React, { useState, useEffect } from 'react';
import { doc, getDoc, collection, getDocs, query, where } from 'firebase/firestore';
import { auth, db } from '../firebaseConfig';
import { createDownloadPackage } from '../lib/callables';
import { useTheme } from '../context/ThemeContext';
import './ExportMyData.css';

interface Submission {
  submissionId: string;
  taskId?: string;
  taskTitle?: string;
  category?: string;
  subcategory?: string;
  mediaUrl?: string;
  labels?: any[];
  labelCount?: number;
  isFullyLabeled?: boolean;
  status?: string;
  createdAt?: any;
  downloadCount?: number;
}

const PRICE_PER_LABEL = 10;

export default function ExportMyData() {
  const { colors } = useTheme();
  const [submissions, setSubmissions] = useState<Submission[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  
  // Filter states
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [selectedSubcategory, setSelectedSubcategory] = useState<string>('all');
  const [selectedTask, setSelectedTask] = useState<string>('all');
  const [showSelectedOnly, setShowSelectedOnly] = useState(false);

  useEffect(() => {
    loadSubmissions();
  }, []);

  const loadSubmissions = async () => {
    if (!auth.currentUser) return;

    setLoading(true);
    try {
      // First, get completed submissions (second approval after labeling) with labels from root submissions collection
      const submissionsRef = collection(db, 'submissions');
      const userSubmissionsQuery = query(
        submissionsRef,
        where('userId', '==', auth.currentUser.uid),
        where('status', '==', 'completed')
      );
      const userSubmissionsSnap = await getDocs(userSubmissionsQuery);
      
      // Create a map of submission ID to submission data with labels
      const rootSubmissionsMap = new Map<string, any>();
      userSubmissionsSnap.forEach((doc) => {
        const subData = doc.data();
        
        // Check for labels - must be a non-empty array
        const labels = subData.labels;
        const hasLabels = labels && 
                         Array.isArray(labels) && 
                         labels.length > 0 &&
                         labels.some((label: any) => label !== null && label !== undefined);
        
        if (hasLabels) {
          rootSubmissionsMap.set(doc.id, {
            id: doc.id,
            submissionId: doc.id,
            ...subData
          });
        } else {
          console.log('Submission without valid labels:', doc.id, {
            hasLabelsField: !!labels,
            isArray: Array.isArray(labels),
            length: Array.isArray(labels) ? labels.length : 'N/A',
            labels: labels
          });
        }
      });

      console.log('Root submissions with valid labels:', rootSubmissionsMap.size, 'out of', userSubmissionsSnap.size);

      // Now get task info from completedTasks
      const userRef = doc(db, 'users', auth.currentUser.uid);
      const userSnap = await getDoc(userRef);
      const data = userSnap.data();
      
      if (!data) {
        setSubmissions([]);
        setLoading(false);
        return;
      }

      const completedTasks = data.completedTasks || {};
      
      // Match root submissions with completedTasks to get task info
      // Also try matching by mediaUrl as a fallback since submissionId might not match
      const allSubmissions: Submission[] = [];
      Object.entries(completedTasks).forEach(([taskId, taskData]: [string, any]) => {
        const taskSubmissions = Array.isArray(taskData.submissions) ? taskData.submissions : [taskData];
        
        taskSubmissions.forEach((sub: any) => {
          // Must be approved (first approval) AND have a completed status in root (second approval after labeling)
          if (sub.status !== 'approved') return;
          
          // Try to find matching root submission by submissionId first
          const submissionId = sub.submissionId || sub.id;
          let rootSubmission = rootSubmissionsMap.get(submissionId);
          
          // If not found by ID, try matching by mediaUrl
          if (!rootSubmission && sub.mediaUrl) {
            const rootSubmissionsArray = Array.from(rootSubmissionsMap.entries());
            for (let i = 0; i < rootSubmissionsArray.length; i++) {
              const [rootId, rootSub] = rootSubmissionsArray[i];
              if (rootSub.mediaUrl === sub.mediaUrl) {
                rootSubmission = rootSub;
                console.log('Matched by mediaUrl:', submissionId, '->', rootId);
                break;
              }
            }
          }
          
          // Must have a root submission with status 'completed' (second approval) and valid labels
          if (rootSubmission && rootSubmission.status === 'completed') {
            // Double-check labels exist and are valid
            const labels = rootSubmission.labels;
            const hasValidLabels = labels && 
                                  Array.isArray(labels) && 
                                  labels.length > 0 &&
                                  labels.some((label: any) => label !== null && label !== undefined);
            
            if (hasValidLabels) {
              const labelCount = labels.length;
              
              allSubmissions.push({
                submissionId: rootSubmission.id || submissionId, // Use root submission ID
                taskId: taskId,
                taskTitle: sub.taskTitle || taskData.taskTitle || rootSubmission.taskTitle,
                category: sub.category || taskData.category || rootSubmission.category || 'N/A',
                subcategory: sub.subcategory || taskData.subcategory || rootSubmission.subcategory || 'N/A',
                mediaUrl: sub.mediaUrl || rootSubmission.mediaUrl,
                labels: labels, // Use labels from root submissions collection
                labelCount: labelCount,
                isFullyLabeled: true,
                status: 'completed', // Use completed status (second approval)
                createdAt: sub.lastSubmittedAt || sub.createdAt || rootSubmission.createdAt
              });
            } else {
              console.log('Skipping submission - no valid labels:', submissionId, {
                labelsType: typeof labels,
                isArray: Array.isArray(labels),
                length: Array.isArray(labels) ? labels.length : 'N/A'
              });
            }
          } else {
            if (rootSubmission) {
              console.log('Skipping submission - not completed (second approval):', submissionId, {
                rootStatus: rootSubmission.status
              });
            } else {
              console.log('No matching root submission found for:', submissionId, {
                hasMediaUrl: !!sub.mediaUrl,
                rootSubmissionsCount: rootSubmissionsMap.size
              });
            }
          }
        });
      });

      console.log('Final submissions to display:', allSubmissions.length);
      if (allSubmissions.length > 0) {
        console.log('Sample submission:', {
          submissionId: allSubmissions[0].submissionId,
          labelCount: allSubmissions[0].labelCount,
          hasLabels: !!allSubmissions[0].labels
        });
      }

      // Load download history to exclude already downloaded items
      const downloadedSubmissionIds = new Set<string>();
      const downloadHistoryMap = new Map<string, number>();
      try {
        const downloadHistoryRef = collection(db, 'userDownloads', auth.currentUser.uid, 'items');
        const downloadHistorySnap = await getDocs(downloadHistoryRef);
        
        downloadHistorySnap.forEach((doc) => {
          const data = doc.data();
          downloadedSubmissionIds.add(doc.id); // Track downloaded submission IDs
          downloadHistoryMap.set(doc.id, data.downloadCount || 0);
        });
      } catch (historyError) {
        // Download history is optional - just log and continue
        console.warn('Could not load download history:', historyError);
      }

      // Filter out already downloaded submissions (they should only appear in My Library)
      const availableSubmissions = allSubmissions.filter(sub => {
        // Check if this submission has been downloaded
        const isDownloaded = downloadedSubmissionIds.has(sub.submissionId);
        
        return !isDownloaded; // Only include if NOT downloaded
      });

      console.log('Filtered out downloaded submissions:', allSubmissions.length - availableSubmissions.length, 'out of', allSubmissions.length);

      // Add download count to submissions (should be 0 for all since we filtered out downloaded ones)
      const submissionsWithHistory = availableSubmissions.map(sub => ({
        ...sub,
        downloadCount: downloadHistoryMap.get(sub.submissionId) || 0
      }));

      setSubmissions(submissionsWithHistory);
    } catch (error) {
      console.error('Error loading submissions:', error);
      setError('Failed to load submissions');
    } finally {
      setLoading(false);
    }
  };

  const toggleSelection = (submissionId: string) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(submissionId)) {
      newSelected.delete(submissionId);
    } else {
      newSelected.add(submissionId);
    }
    setSelectedIds(newSelected);
  };

  const selectAll = () => {
    const filtered = getFilteredSubmissions();
    const filteredIds = new Set(filtered.map(s => s.submissionId));
    
    if (filteredIds.size > 0 && Array.from(filteredIds).every(id => selectedIds.has(id))) {
      // Deselect all filtered items
      const newSelected = new Set(selectedIds);
      filteredIds.forEach(id => newSelected.delete(id));
      setSelectedIds(newSelected);
    } else {
      // Select all filtered items
      const newSelected = new Set(selectedIds);
      filteredIds.forEach(id => newSelected.add(id));
      setSelectedIds(newSelected);
    }
  };
  
  // Get unique categories and subcategories
  const getUniqueCategories = () => {
    const categories = new Set<string>();
    submissions.forEach(s => {
      if (s.category && s.category !== 'N/A') {
        categories.add(s.category);
      }
    });
    return Array.from(categories).sort();
  };
  
  const getUniqueSubcategories = () => {
    const subcategories = new Set<string>();
    submissions.forEach(s => {
      if (s.subcategory && s.subcategory !== 'N/A') {
        subcategories.add(s.subcategory);
      }
    });
    return Array.from(subcategories).sort();
  };
  
  // Filter submissions based on search and filters
  const getFilteredSubmissions = () => {
    return submissions.filter(sub => {
      // Show selected only filter
      if (showSelectedOnly && !selectedIds.has(sub.submissionId)) {
        return false;
      }
      
      // Category filter
      if (selectedCategory !== 'all' && sub.category !== selectedCategory) {
        return false;
      }
      
      // Subcategory filter
      if (selectedSubcategory !== 'all' && sub.subcategory !== selectedSubcategory) {
        return false;
      }
      
      // General search filter
      if (searchQuery.trim()) {
        const query = searchQuery.toLowerCase();
        const taskTitle = (sub.taskTitle || '').toLowerCase();
        const taskId = (sub.taskId || '').toLowerCase();
        const category = (sub.category || '').toLowerCase();
        const subcategory = (sub.subcategory || '').toLowerCase();
        
        if (!taskTitle.includes(query) && 
            !taskId.includes(query) && 
            !category.includes(query) && 
            !subcategory.includes(query)) {
          return false;
        }
      }
      
      // Task filter
      if (selectedTask !== 'all' && sub.taskId !== selectedTask) {
        return false;
      }
      
      return true;
    });
  };
  
  // Check if any filters are active
  const hasActiveFilters = () => {
    return searchQuery.trim() !== '' || 
           selectedCategory !== 'all' || 
           selectedSubcategory !== 'all' || 
           selectedTask !== 'all' ||
           showSelectedOnly;
  };
  
  // Clear all filters
  const clearAllFilters = () => {
    setSearchQuery('');
    setSelectedCategory('all');
    setSelectedSubcategory('all');
    setSelectedTask('all');
    setShowSelectedOnly(false);
  };
  
  // Get unique tasks (filtered by category and subcategory if selected)
  const getUniqueTasks = () => {
    const taskMap = new Map<string, { title: string; id: string }>();
    submissions.forEach(s => {
      // Filter by category if selected
      if (selectedCategory !== 'all' && s.category !== selectedCategory) {
        return;
      }
      // Filter by subcategory if selected
      if (selectedSubcategory !== 'all' && s.subcategory !== selectedSubcategory) {
        return;
      }
      if (s.taskId && s.taskTitle) {
        taskMap.set(s.taskId, { title: s.taskTitle, id: s.taskId });
      }
    });
    return Array.from(taskMap.values()).sort((a, b) => a.title.localeCompare(b.title));
  };

  const getSelectedStats = () => {
    // Only count selected items that are currently visible (filtered)
    const filtered = getFilteredSubmissions();
    const selected = filtered.filter(s => selectedIds.has(s.submissionId));
    const totalLabels = selected.reduce((sum, s) => sum + (s.labelCount || 0), 0);
    const totalPrice = totalLabels * PRICE_PER_LABEL;
    return { count: selected.length, totalLabels, totalPrice };
  };

  const handleDownload = async () => {
    if (selectedIds.size === 0) {
      setError('Please select at least one submission');
      return;
    }

    setShowConfirmModal(true);
  };

  const confirmDownload = async () => {
    setShowConfirmModal(false);
    setDownloading(true);
    setError(null);
    setDownloadUrl(null);

    try {
      const submissionIds = Array.from(selectedIds);
      const result = await createDownloadPackage(submissionIds);
      
      setDownloadUrl(result.downloadUrl);
      
      // Reload submissions to update download counts
      await loadSubmissions();
      
      // Clear selection
      setSelectedIds(new Set());
    } catch (error: any) {
      console.error('Error creating download package:', error);
      setError(error.message || 'Failed to create download package');
    } finally {
      setDownloading(false);
    }
  };

  const formatDate = (timestamp: any) => {
    if (!timestamp) return 'N/A';
    
    try {
      if (timestamp.toDate) {
        return timestamp.toDate().toLocaleDateString();
      }
      if (timestamp.seconds) {
        return new Date(timestamp.seconds * 1000).toLocaleDateString();
      }
      if (typeof timestamp === 'number') {
        return new Date(timestamp).toLocaleDateString();
      }
      return 'N/A';
    } catch {
      return 'N/A';
    }
  };

  const stats = getSelectedStats();

  if (loading) {
    return (
      <div className="export-page" style={{ background: colors.background, color: colors.text }}>
        <div className="export-container">
          <div className="loading">Loading your submissions...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="export-page" style={{ background: colors.background, color: colors.text }}>
      <div className="export-container">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '10px' }}>
          <div style={{ flex: 1 }}>
            <h1 style={{ color: colors.text, margin: 0 }}>Download My Data</h1>
            <p className="export-description" style={{ color: colors.secondary, margin: '10px 0 0 0' }}>
              Select your approved and fully labeled submissions to download. Each label costs {PRICE_PER_LABEL} PIX.
            </p>
          </div>
          <button
            onClick={selectAll}
            className="select-all-btn-header"
            style={{
              background: colors.card,
              borderColor: colors.border,
              color: colors.text,
              padding: '10px 20px',
              borderRadius: '6px',
              border: '1px solid',
              fontSize: '14px',
              cursor: 'pointer',
              whiteSpace: 'nowrap',
              marginLeft: '20px',
              fontWeight: '500'
            }}
            title="Select or deselect all visible items"
          >
            {(() => {
              const filtered = getFilteredSubmissions();
              const allFilteredSelected = filtered.length > 0 && 
                filtered.every(s => selectedIds.has(s.submissionId));
              return allFilteredSelected ? 'Deselect All' : 'Select All';
            })()}
          </button>
        </div>

        {error && (
          <div className="error-message" style={{ background: colors.error + '20', color: colors.error }}>
            {error}
          </div>
        )}

        {downloadUrl && (
          <div className="success-message" style={{ background: colors.primary + '20', color: colors.primary }}>
            <p>Download ready! <a href={downloadUrl} download style={{ color: colors.primary, textDecoration: 'underline' }}>Click here to download</a></p>
          </div>
        )}

        {submissions.length === 0 ? (
          <div className="empty-state" style={{ color: colors.secondary }}>
            No approved and fully labeled submissions found.
          </div>
        ) : (
          <>
            {/* Filters and Search */}
            <div className="filters-section" style={{ marginBottom: '20px' }}>
              <div className="filters-row">
                <div className="filter-group">
                  <label style={{ color: colors.secondary, fontSize: '12px', marginBottom: '4px', display: 'block' }}>
                    Search
                  </label>
                  <input
                    type="text"
                    placeholder="Search by task name, ID, category..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="search-input"
                    style={{
                      background: colors.card,
                      borderColor: colors.border,
                      color: colors.text,
                      padding: '8px 12px',
                      borderRadius: '6px',
                      border: '1px solid',
                      fontSize: '14px',
                      width: '100%',
                      maxWidth: '300px'
                    }}
                  />
                </div>
                
                <div className="filter-group">
                  <label style={{ color: colors.secondary, fontSize: '12px', marginBottom: '4px', display: 'block' }}>
                    Category
                  </label>
                  <select
                    value={selectedCategory}
                    onChange={(e) => {
                      setSelectedCategory(e.target.value);
                      setSelectedSubcategory('all'); // Reset subcategory when category changes
                      setSelectedTask('all'); // Reset task when category changes
                    }}
                    className="filter-select"
                    style={{
                      background: colors.card,
                      borderColor: colors.border,
                      color: colors.text,
                      padding: '8px 12px',
                      borderRadius: '6px',
                      border: '1px solid',
                      fontSize: '14px',
                      cursor: 'pointer'
                    }}
                  >
                    <option value="all">All Categories</option>
                    {getUniqueCategories().map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>
                
                <div className="filter-group">
                  <label style={{ color: colors.secondary, fontSize: '12px', marginBottom: '4px', display: 'block' }}>
                    Subcategory
                  </label>
                  <select
                    value={selectedSubcategory}
                    onChange={(e) => {
                      setSelectedSubcategory(e.target.value);
                      setSelectedTask('all'); // Reset task when subcategory changes
                    }}
                    className="filter-select"
                    style={{
                      background: colors.card,
                      borderColor: colors.border,
                      color: colors.text,
                      padding: '8px 12px',
                      borderRadius: '6px',
                      border: '1px solid',
                      fontSize: '14px',
                      cursor: 'pointer'
                    }}
                    disabled={selectedCategory === 'all'}
                  >
                    <option value="all">All Subcategories</option>
                    {getUniqueSubcategories()
                      .filter(sub => selectedCategory === 'all' || 
                        submissions.some(s => s.category === selectedCategory && s.subcategory === sub))
                      .map(sub => (
                        <option key={sub} value={sub}>{sub}</option>
                      ))}
                  </select>
                </div>
                
                <div className="filter-group">
                  <label style={{ color: colors.secondary, fontSize: '12px', marginBottom: '4px', display: 'block' }}>
                    Task
                  </label>
                  <select
                    value={selectedTask}
                    onChange={(e) => setSelectedTask(e.target.value)}
                    className="filter-select"
                    style={{
                      background: colors.card,
                      borderColor: colors.border,
                      color: colors.text,
                      padding: '8px 12px',
                      borderRadius: '6px',
                      border: '1px solid',
                      fontSize: '14px',
                      cursor: 'pointer',
                      minWidth: '200px'
                    }}
                    disabled={selectedCategory === 'all' && selectedSubcategory === 'all'}
                  >
                    <option value="all">All Tasks</option>
                    {getUniqueTasks().map(task => (
                      <option key={task.id} value={task.id}>{task.title}</option>
                    ))}
                  </select>
                </div>
                
                <div className="filter-group" style={{ display: 'flex', alignItems: 'flex-end' }}>
                  <button
                    onClick={() => setShowSelectedOnly(!showSelectedOnly)}
                    className="show-selected-btn"
                    style={{
                      background: showSelectedOnly ? colors.primary : colors.card,
                      borderColor: colors.border,
                      color: showSelectedOnly ? '#fff' : colors.text,
                      padding: '8px 16px',
                      borderRadius: '6px',
                      border: '1px solid',
                      fontSize: '14px',
                      cursor: 'pointer',
                      whiteSpace: 'nowrap',
                      fontWeight: showSelectedOnly ? '600' : '400'
                    }}
                  >
                    Show Selected ({selectedIds.size})
                  </button>
                </div>
              </div>
              
              {/* Filter Indicators */}
              {hasActiveFilters() && (
                <div className="filter-indicators" style={{ 
                  marginTop: '12px', 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '8px',
                  flexWrap: 'wrap'
                }}>
                  <span style={{ color: colors.secondary, fontSize: '12px', fontWeight: '500' }}>
                    Active Filters:
                  </span>
                  
                  {searchQuery.trim() && (
                    <span className="filter-badge" style={{
                      background: colors.primary + '20',
                      color: colors.primary,
                      padding: '4px 10px',
                      borderRadius: '12px',
                      fontSize: '12px',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '6px'
                    }}>
                      Search: "{searchQuery}"
                      <button
                        onClick={() => setSearchQuery('')}
                        style={{
                          background: 'transparent',
                          border: 'none',
                          color: colors.primary,
                          cursor: 'pointer',
                          padding: '0',
                          marginLeft: '4px',
                          fontSize: '14px',
                          lineHeight: '1'
                        }}
                        title="Remove filter"
                      >
                        ×
                      </button>
                    </span>
                  )}
                  
                  {selectedCategory !== 'all' && (
                    <span className="filter-badge" style={{
                      background: colors.primary + '20',
                      color: colors.primary,
                      padding: '4px 10px',
                      borderRadius: '12px',
                      fontSize: '12px',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '6px'
                    }}>
                      Category: {selectedCategory}
                      <button
                        onClick={() => {
                          setSelectedCategory('all');
                          setSelectedSubcategory('all');
                        }}
                        style={{
                          background: 'transparent',
                          border: 'none',
                          color: colors.primary,
                          cursor: 'pointer',
                          padding: '0',
                          marginLeft: '4px',
                          fontSize: '14px',
                          lineHeight: '1'
                        }}
                        title="Remove filter"
                      >
                        ×
                      </button>
                    </span>
                  )}
                  
                  {selectedSubcategory !== 'all' && (
                    <span className="filter-badge" style={{
                      background: colors.primary + '20',
                      color: colors.primary,
                      padding: '4px 10px',
                      borderRadius: '12px',
                      fontSize: '12px',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '6px'
                    }}>
                      Subcategory: {selectedSubcategory}
                      <button
                        onClick={() => setSelectedSubcategory('all')}
                        style={{
                          background: 'transparent',
                          border: 'none',
                          color: colors.primary,
                          cursor: 'pointer',
                          padding: '0',
                          marginLeft: '4px',
                          fontSize: '14px',
                          lineHeight: '1'
                        }}
                        title="Remove filter"
                      >
                        ×
                      </button>
                    </span>
                  )}
                  
                  {selectedTask !== 'all' && (
                    <span className="filter-badge" style={{
                      background: colors.primary + '20',
                      color: colors.primary,
                      padding: '4px 10px',
                      borderRadius: '12px',
                      fontSize: '12px',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '6px'
                    }}>
                      Task: {getUniqueTasks().find(t => t.id === selectedTask)?.title || selectedTask}
                      <button
                        onClick={() => setSelectedTask('all')}
                        style={{
                          background: 'transparent',
                          border: 'none',
                          color: colors.primary,
                          cursor: 'pointer',
                          padding: '0',
                          marginLeft: '4px',
                          fontSize: '14px',
                          lineHeight: '1'
                        }}
                        title="Remove filter"
                      >
                        ×
                      </button>
                    </span>
                  )}
                  
                  {showSelectedOnly && (
                    <span className="filter-badge" style={{
                      background: colors.primary + '20',
                      color: colors.primary,
                      padding: '4px 10px',
                      borderRadius: '12px',
                      fontSize: '12px',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '6px'
                    }}>
                      Show Selected Only
                      <button
                        onClick={() => setShowSelectedOnly(false)}
                        style={{
                          background: 'transparent',
                          border: 'none',
                          color: colors.primary,
                          cursor: 'pointer',
                          padding: '0',
                          marginLeft: '4px',
                          fontSize: '14px',
                          lineHeight: '1'
                        }}
                        title="Remove filter"
                      >
                        ×
                      </button>
                    </span>
                  )}
                  
                  <button
                    onClick={clearAllFilters}
                    className="clear-filters-btn"
                    style={{
                      background: 'transparent',
                      borderColor: colors.border,
                      color: colors.secondary,
                      padding: '4px 12px',
                      borderRadius: '6px',
                      border: '1px solid',
                      fontSize: '12px',
                      cursor: 'pointer',
                      whiteSpace: 'nowrap',
                      marginLeft: '8px'
                    }}
                  >
                    Clear All Filters
                  </button>
                </div>
              )}
            </div>

            <div className="submissions-table">
              <table>
                <thead>
                  <tr style={{ background: colors.card, borderColor: colors.border }}>
                    <th style={{ borderColor: colors.border }}>Select</th>
                    <th style={{ borderColor: colors.border }}>Thumbnail</th>
                    <th style={{ borderColor: colors.border }}>Category</th>
                    <th style={{ borderColor: colors.border }}>Subcategory</th>
                    <th style={{ borderColor: colors.border }}>Task</th>
                    <th style={{ borderColor: colors.border }}>Labels</th>
                    <th style={{ borderColor: colors.border }}>Approved</th>
                    <th style={{ borderColor: colors.border }}>Cost</th>
                  </tr>
                </thead>
                <tbody>
                  {getFilteredSubmissions().map((sub) => {
                    const isSelected = selectedIds.has(sub.submissionId);
                    const cost = (sub.labelCount || 0) * PRICE_PER_LABEL;

                    return (
                      <tr
                        key={sub.submissionId}
                        style={{
                          background: isSelected ? colors.primary + '10' : colors.card,
                          borderColor: colors.border
                        }}
                      >
                        <td style={{ borderColor: colors.border }}>
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => toggleSelection(sub.submissionId)}
                            style={{ cursor: 'pointer' }}
                          />
                        </td>
                        <td style={{ borderColor: colors.border }}>
                          {sub.mediaUrl ? (
                            <img
                              src={sub.mediaUrl}
                              alt="Submission"
                              className="submission-thumbnail"
                              onError={(e) => {
                                (e.target as HTMLImageElement).style.display = 'none';
                              }}
                            />
                          ) : (
                            <div className="no-thumbnail" style={{ background: colors.border }}>
                              No Image
                            </div>
                          )}
                        </td>
                        <td style={{ borderColor: colors.border }}>
                          {sub.category && sub.category !== 'N/A' ? sub.category : '-'}
                        </td>
                        <td style={{ borderColor: colors.border }}>
                          {sub.subcategory && sub.subcategory !== 'N/A' ? sub.subcategory : '-'}
                        </td>
                        <td style={{ borderColor: colors.border }}>
                          <div className="task-info">
                            <div className="task-title">{sub.taskTitle || 'Unknown Task'}</div>
                            <div className="task-id" style={{ color: colors.secondary, fontSize: '12px' }}>
                              {sub.taskId}
                            </div>
                          </div>
                        </td>
                        <td style={{ borderColor: colors.border }}>
                          {sub.labelCount || 0}
                        </td>
                        <td style={{ borderColor: colors.border }}>
                          {formatDate(sub.createdAt)}
                        </td>
                        <td style={{ borderColor: colors.border }}>
                          {cost} PIX
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Sticky Footer */}
            <div
              className="sticky-footer"
              style={{
                background: colors.card,
                borderColor: colors.border
              }}
            >
              <div className="footer-stats">
                <div className="stat-item">
                  <span className="stat-label" style={{ color: colors.secondary }}>Selected:</span>
                  <span className="stat-value" style={{ color: colors.text }}>{stats.count}</span>
                </div>
                <div className="stat-item">
                  <span className="stat-label" style={{ color: colors.secondary }}>Total Labels:</span>
                  <span className="stat-value" style={{ color: colors.text }}>{stats.totalLabels}</span>
                </div>
                <div className="stat-item">
                  <span className="stat-label" style={{ color: colors.secondary }}>Total Cost:</span>
                  <span className="stat-value" style={{ color: colors.primary, fontWeight: 'bold' }}>
                    {stats.totalPrice} PIX
                  </span>
                </div>
              </div>
              <button
                onClick={handleDownload}
                disabled={selectedIds.size === 0 || downloading}
                className="download-btn"
                style={{
                  background: selectedIds.size === 0 || downloading ? colors.border : colors.primary,
                  color: '#fff',
                  opacity: selectedIds.size === 0 || downloading ? 0.6 : 1,
                  cursor: selectedIds.size === 0 || downloading ? 'not-allowed' : 'pointer'
                }}
              >
                {downloading ? 'Processing...' : 'Download Selected'}
              </button>
            </div>
          </>
        )}

        {/* Confirmation Modal */}
        {showConfirmModal && (
          <div className="modal-overlay" onClick={() => setShowConfirmModal(false)}>
            <div
              className="modal-content"
              style={{ background: colors.card, borderColor: colors.border }}
              onClick={(e) => e.stopPropagation()}
            >
              <h2 style={{ color: colors.text }}>Confirm Download</h2>
              <p style={{ color: colors.secondary }}>
                You are downloading <strong>{stats.totalLabels} labels</strong> for <strong>{stats.totalPrice} PIX</strong>.
              </p>
              <p style={{ color: colors.secondary, fontSize: '14px' }}>
                This will deduct {stats.totalPrice} PIX from your balance.
              </p>
              <div className="modal-actions">
                <button
                  onClick={() => setShowConfirmModal(false)}
                  className="modal-btn cancel"
                  style={{
                    background: colors.border,
                    color: colors.text
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDownload}
                  className="modal-btn confirm"
                  style={{
                    background: colors.primary,
                    color: '#fff'
                  }}
                >
                  Confirm
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

